/// <reference types="vite-plugin-svgr/client" />

import "./App.scss";
import "./i18n/i18n";

import { useEffect } from "react";
import { Toaster } from "react-hot-toast";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import CookieBanner from "./components/CookieBanner/CookieBanner";
import CustomCursor from "./components/CustomCursor/CustomCursor";
import Footer from "./components/Footer/Footer";
import SeoHead from "./components/SeoHead/SeoHead";
import LanguageSwitcher from "./components/LanguageSwitcher/LanguageSwitcher";
import useNavItems from "./hooks/useNavItems";
import { Navbar } from "./mcn-react-ui";
import Home from "./pages/Home/Home";
import LinkedInBanner from "./pages/LinkedInBanner/LinkedInBanner";
import XBanner from "./pages/XBanner/XBanner";
import { LinkedInPostExport } from "./pages/LinkedInPost/LinkedInPost";
import { ResumePdfExport } from "./pages/Resume/Resume";

const SUPPORTED_LANGS = ["en", "fr"] as const;
type SupportedLang = (typeof SUPPORTED_LANGS)[number];

function detectLang(): SupportedLang {
  const stored = localStorage.getItem("i18nextLng");
  if (stored && SUPPORTED_LANGS.includes(stored as SupportedLang)) {
    return stored as SupportedLang;
  }
  const browser = navigator.language.split("-")[0];
  return SUPPORTED_LANGS.includes(browser as SupportedLang)
    ? (browser as SupportedLang)
    : "en";
}

function LangRedirect() {
  return <Navigate to={`/${detectLang()}/`} replace />;
}

function LocalizedApp() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const navItems = useNavItems();

  const validLang = SUPPORTED_LANGS.includes(lang as SupportedLang)
    ? (lang as SupportedLang)
    : null;

  useEffect(() => {
    if (!validLang) {
      navigate("/", { replace: true });
      return;
    }
    if (i18n.resolvedLanguage !== validLang) {
      i18n.changeLanguage(validLang);
    }
    localStorage.setItem("i18nextLng", validLang);
    document.documentElement.lang = validLang;
  }, [validLang, i18n, navigate]);

  if (!validLang) return null;

  return (
    <div className="App">
      <SeoHead />
      <CustomCursor />
      <Navbar
        navItems={navItems}
        layout="top"
        mobileBeforeChildren={<LanguageSwitcher mobile />}
      >
        <LanguageSwitcher />
      </Navbar>
      <main>
        <Routes>
          <Route index element={<Home />} />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
      <Toaster position="bottom-right" reverseOrder={false} />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        {import.meta.env.DEV && <Route path="/linkedin-banner" element={<LinkedInBanner />} />}
        {import.meta.env.DEV && <Route path="/x-banner" element={<XBanner />} />}
        {import.meta.env.DEV && <Route path="/linkedin-post/export" element={<LinkedInPostExport />} />}
        {import.meta.env.DEV && <Route path="/resume/export" element={<ResumePdfExport />} />}
        <Route path="/:lang/*" element={<LocalizedApp />} />
        <Route index element={<LangRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
